import {
    Box,
    Flex,
    Grid,
    HStack,
    SimpleGrid,
    Skeleton,
    SkeletonCircle,
    Stack,
} from '@chakra-ui/react';
import * as React from 'react';

export const MentorshipHeaderSkeleton: React.FC<{
  withActions?: boolean;
}> = ({ withActions = true }) => (
  <Flex
    align={{ base: 'stretch', md: 'center' }}
    justify="space-between"
    direction={{ base: 'column', md: 'row' }}
    gap="4"
  >
    <Stack spacing="2">
      <Skeleton h="24px" w={{ base: '210px', md: '280px' }} />
      <Skeleton h="16px" w={{ base: '280px', md: '420px' }} maxW="full" />
    </Stack>
    {withActions ? <Skeleton h="36px" w="180px" borderRadius="md" /> : null}
  </Flex>
);

export const MentorshipPersonCardSkeleton: React.FC<{
  showSections?: boolean;
}> = ({ showSections = false }) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor="gray.100"
    borderRadius="lg"
    boxShadow="sm"
    p="5"
  >
    <Flex align="flex-start" gap="4">
      <SkeletonCircle size="16" flexShrink={0} />
      <Stack spacing="2" flex="1" minW="0">
        <HStack>
          <Skeleton h="20px" w="45%" />
          <Skeleton h="20px" w="64px" borderRadius="full" />
        </HStack>
        <Skeleton h="14px" w="70%" />
      </Stack>
    </Flex>
    {showSections ? (
      <Stack spacing="4" mt="5">
        <Stack spacing="2">
          <Skeleton h="12px" w="110px" />
          <Skeleton h="22px" w="55%" borderRadius="full" />
        </Stack>
        <Stack spacing="2">
          <Skeleton h="12px" w="130px" />
          <Skeleton h="14px" w="90%" />
          <Skeleton h="14px" w="72%" />
        </Stack>
        <Flex justify="flex-end">
          <Skeleton h="32px" w="120px" borderRadius="md" />
        </Flex>
      </Stack>
    ) : null}
  </Box>
);

export const MentorshipCardGridSkeleton: React.FC<{
  count?: number;
  detailed?: boolean;
}> = ({ count = 4, detailed = false }) => (
  <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="4">
    {Array.from({ length: count }, (_, index) => (
      <MentorshipPersonCardSkeleton key={index} showSections={detailed} />
    ))}
  </SimpleGrid>
);

export const MentorshipWorkspaceSkeleton: React.FC = () => (
  <Stack spacing="5">
    <Box
      bg="white"
      borderWidth="1px"
      borderColor="gray.100"
      borderRadius="lg"
      boxShadow="sm"
      p="5"
    >
      <Flex
        justify="space-between"
        align={{ base: 'stretch', md: 'flex-start' }}
        direction={{ base: 'column', md: 'row' }}
        gap="5"
      >
        <Stack spacing="3" flex="1">
          <HStack>
            <Skeleton h="22px" w="260px" maxW="65%" />
            <Skeleton h="20px" w="58px" borderRadius="full" />
          </HStack>
          <Skeleton h="15px" w="80%" />
          <Skeleton h="15px" w="62%" />
          <HStack>
            <Skeleton h="20px" w="52px" borderRadius="full" />
            <Skeleton h="20px" w="90px" borderRadius="full" />
          </HStack>
        </Stack>
        <HStack alignSelf={{ base: 'flex-end', md: 'flex-start' }}>
          <Skeleton h="32px" w="36px" borderRadius="md" />
          <Skeleton h="32px" w="36px" borderRadius="md" />
        </HStack>
      </Flex>
    </Box>
    <HStack spacing="5" overflow="hidden">
      {[88, 76, 62, 68, 92].map(width => (
        <Skeleton key={width} h="34px" w={`${width}px`} flexShrink={0} />
      ))}
    </HStack>
    <MentorshipCardGridSkeleton count={2} />
  </Stack>
);

export const MentorshipTableSkeleton: React.FC<{
  rows?: number;
  columns?: number;
}> = ({ rows = 5, columns = 5 }) => (
  <Box bg="white" borderWidth="1px" borderColor="gray.100" borderRadius="lg">
    <Grid
      templateColumns={`repeat(${columns}, minmax(90px, 1fr))`}
      gap="4"
      px="4"
      py="3"
      borderBottomWidth="1px"
      borderColor="gray.100"
      overflow="hidden"
    >
      {Array.from({ length: columns }, (_, index) => (
        <Skeleton key={index} h="12px" w="70%" />
      ))}
    </Grid>
    {Array.from({ length: rows }, (_, row) => (
      <Grid
        key={row}
        templateColumns={`repeat(${columns}, minmax(90px, 1fr))`}
        gap="4"
        px="4"
        py="4"
        borderBottomWidth={row === rows - 1 ? 0 : '1px'}
        borderColor="gray.100"
        overflow="hidden"
      >
        {Array.from({ length: columns }, (_, column) => (
          <Skeleton key={column} h="16px" w={column === 0 ? '88%' : '60%'} />
        ))}
      </Grid>
    ))}
  </Box>
);

export const MentorshipDetailSkeleton: React.FC = () => (
  <Stack spacing="5" maxW="960px">
    <MentorshipPersonCardSkeleton />
    {[3, 4].map(lines => (
      <Box
        key={lines}
        bg="white"
        borderWidth="1px"
        borderColor="gray.100"
        borderRadius="lg"
        p="5"
      >
        <Skeleton h="18px" w="180px" mb="5" />
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing="4">
          {Array.from({ length: lines }, (_, index) => (
            <Stack key={index} spacing="2">
              <Skeleton h="12px" w="90px" />
              <Skeleton h="16px" w="75%" />
            </Stack>
          ))}
        </SimpleGrid>
      </Box>
    ))}
  </Stack>
);

export const MentorshipDashboardSkeleton: React.FC = () => (
  <Stack spacing="5">
    <MentorshipHeaderSkeleton />
    <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="3">
      {Array.from({ length: 4 }, (_, index) => (
        <Box
          key={index}
          bg="white"
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius="xl"
          p="4"
        >
          <HStack spacing="3">
            <Skeleton h="42px" w="42px" borderRadius="lg" />
            <Stack spacing="2" flex="1">
              <Skeleton h="12px" w="70%" />
              <Skeleton h="24px" w="45%" />
              <Skeleton h="10px" w="90%" />
            </Stack>
          </HStack>
        </Box>
      ))}
    </SimpleGrid>
    <SimpleGrid columns={{ base: 1, xl: 2 }} spacing="3">
      {[0, 1].map(index => (
        <Box key={index} bg="white" borderRadius="xl" p="5">
          <HStack justify="space-between" mb="5">
            <Stack spacing="2" flex="1">
              <Skeleton h="18px" w="150px" />
              <Skeleton h="12px" w="220px" maxW="80%" />
            </Stack>
            <Skeleton h="24px" w="60px" />
          </HStack>
          <Stack spacing="3">
            {[0, 1, 2].map(row => (
              <HStack key={row} spacing="3">
                <SkeletonCircle size="9" />
                <Stack spacing="2" flex="1">
                  <Skeleton h="14px" w="55%" />
                  <Skeleton h="11px" w="35%" />
                </Stack>
              </HStack>
            ))}
          </Stack>
        </Box>
      ))}
    </SimpleGrid>
  </Stack>
);

export const MentorshipProgramCardSkeleton: React.FC = () => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor="gray.100"
    borderRadius="lg"
    boxShadow="sm"
    p="5"
  >
    <Flex justify="space-between" align="flex-start" gap="3">
      <Stack spacing="2" flex="1">
        <Skeleton h="20px" w="72%" />
        <Skeleton h="14px" w="46%" />
      </Stack>
      <Skeleton h="20px" w="64px" borderRadius="full" />
    </Flex>
    <Stack spacing="3" mt="5">
      <Skeleton h="14px" w="94%" />
      <Skeleton h="14px" w="78%" />
      <SimpleGrid columns={2} spacing="4" pt="2">
        {[0, 1, 2, 3].map(item => (
          <Stack key={item} spacing="2">
            <Skeleton h="11px" w="65%" />
            <Skeleton h="15px" w="82%" />
          </Stack>
        ))}
      </SimpleGrid>
    </Stack>
    <Flex justify="flex-end" mt="5">
      <Skeleton h="34px" w="112px" borderRadius="md" />
    </Flex>
  </Box>
);

export const MentorshipProgramGridSkeleton: React.FC<{
  count?: number;
}> = ({ count = 2 }) => (
  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="5">
    {Array.from({ length: count }, (_, index) => (
      <MentorshipProgramCardSkeleton key={index} />
    ))}
  </SimpleGrid>
);

export const MentorshipProgramListSkeleton: React.FC = () => (
  <Stack spacing="5">
    <Flex justify="flex-end">
      <Skeleton h="32px" w="132px" borderRadius="md" />
    </Flex>
    <SimpleGrid columns={{ base: 2, md: 4 }} spacing="4">
      {Array.from({ length: 4 }, (_, index) => (
        <Box
          key={index}
          bg="white"
          borderWidth="1px"
          borderColor="gray.100"
          borderRadius="lg"
          p="4"
        >
          <HStack>
            <Skeleton h="32px" w="32px" borderRadius="md" />
            <Stack spacing="2" flex="1">
              <Skeleton h="11px" w="75%" />
              <Skeleton h="20px" w="45%" />
            </Stack>
          </HStack>
        </Box>
      ))}
    </SimpleGrid>
    <Flex gap="2" wrap="wrap">
      <Skeleton h="32px" w="260px" borderRadius="md" />
      <Skeleton h="32px" w="36px" borderRadius="md" />
      <Skeleton h="32px" w="160px" borderRadius="md" />
    </Flex>
    <MentorshipTableSkeleton rows={5} columns={8} />
  </Stack>
);

export const MentorshipCompactListSkeleton: React.FC<{
  rows?: number;
  avatar?: boolean;
}> = ({ rows = 3, avatar = false }) => (
  <Stack spacing="0">
    {Array.from({ length: rows }, (_, index) => (
      <Flex
        key={index}
        align="center"
        gap="3"
        py="3"
        borderBottomWidth={index === rows - 1 ? 0 : '1px'}
        borderColor="gray.100"
      >
        {avatar ? (
          <SkeletonCircle size="9" flexShrink={0} />
        ) : (
          <Skeleton h="38px" w="76px" borderRadius="md" flexShrink={0} />
        )}
        <Stack spacing="2" flex="1">
          <Skeleton h="14px" w={index % 2 ? '58%' : '72%'} />
          <Skeleton h="11px" w={index % 2 ? '38%' : '48%'} />
        </Stack>
        <Skeleton h="20px" w="62px" borderRadius="full" />
      </Flex>
    ))}
  </Stack>
);
